import { request } from '@/shared/api/http-client'
import { emptyEnvelopeSchema } from '@/shared/api/api-contract'
import { fileEntryListEnvelopeSchema, fileEnvelopeSchema, fileFolderEnvelopeSchema, fileFolderTreeEnvelopeSchema, fileListEnvelopeSchema, filePreviewEnvelopeSchema, fileTagEnvelopeSchema, fileTagListEnvelopeSchema, fileUploadEnvelopeSchema } from './file-contract'
import type { FileAsset } from './file-contract'

export interface ListFileAssetsParams {
  page?: number
  size?: number
  businessType?: FileAsset['businessType']
  mediaType?: FileAsset['mediaType']
  status?: FileAsset['status']
  favorite?: boolean
  tagId?: number
  scope?: 'CURRENT' | 'ALL'
  parentFolderId?: number
  showFolders?: boolean
}

function fileListQuery(params: ListFileAssetsParams) {
  const query = new URLSearchParams({
    page: String(params.page ?? 1),
    size: String(params.size ?? 20),
    status: params.status ?? 'AVAILABLE',
  })
  if (params.businessType) query.set('businessType', params.businessType)
  if (params.mediaType) query.set('mediaType', params.mediaType)
  if (params.favorite !== undefined) query.set('favorite', String(params.favorite))
  if (params.tagId !== undefined) query.set('tagId', String(params.tagId))
  if (params.scope) query.set('scope', params.scope)
  if (params.parentFolderId !== undefined) query.set('parentFolderId', String(params.parentFolderId))
  if (params.showFolders !== undefined) query.set('showFolders', String(params.showFolders))
  return query
}

export async function uploadFileAsset(file: File, businessType: 'AVATAR' | 'AI_INPUT' = 'AI_INPUT') {
  const upload = await request('/files/uploads', {
    method: 'POST',
    body: { businessType, filename: file.name, contentType: file.type || 'application/octet-stream', contentLength: file.size },
    schema: fileUploadEnvelopeSchema,
  })
  const uploadResponse = await fetch(upload.data.uploadUrl, { method: upload.data.uploadMethod, headers: upload.data.headers, body: file })
  if (!uploadResponse.ok) throw new Error(`文件上传失败：${uploadResponse.status}`)
  const completed = await request(`/files/${upload.data.fileId}/complete`, { method: 'POST', schema: fileEnvelopeSchema })
  return completed.data
}

export function listFileAssets(params: ListFileAssetsParams = {}) {
  const query = fileListQuery({ scope: 'ALL', showFolders: false, ...params })
  return request(`/files?${query}`, { method: 'GET', schema: fileListEnvelopeSchema })
}

export function listFileEntries(params: ListFileAssetsParams = {}) {
  const query = fileListQuery({ scope: 'CURRENT', parentFolderId: 0, showFolders: true, ...params })
  return request(`/files?${query}`, { method: 'GET', schema: fileEntryListEnvelopeSchema })
}

export function listFileTags() {
  return request('/files/tags', { method: 'GET', schema: fileTagListEnvelopeSchema })
}

export function createFileTag(name: string) {
  return request('/files/tags', { method: 'POST', body: { name }, schema: fileTagEnvelopeSchema })
}

export function deleteFileTag(tagId: number) {
  return request(`/files/tags/${tagId}`, { method: 'DELETE', schema: emptyEnvelopeSchema })
}

export function replaceFileTags(fileId: number, tagIds: number[]) {
  return request(`/files/${fileId}/tags`, { method: 'PUT', body: { tagIds }, schema: emptyEnvelopeSchema })
}

export function getFilePreview(fileId: number) {
  return request(`/files/${fileId}/preview`, { method: 'GET', schema: filePreviewEnvelopeSchema })
}

export function favoriteFileAsset(fileId: number) {
  return request(`/files/${fileId}/favorite`, { method: 'PUT', schema: emptyEnvelopeSchema })
}

export function unfavoriteFileAsset(fileId: number) {
  return request(`/files/${fileId}/favorite`, { method: 'DELETE', schema: emptyEnvelopeSchema })
}

export function deleteFileAsset(fileId: number) {
  return request(`/files/${fileId}`, { method: 'DELETE', schema: emptyEnvelopeSchema })
}

export function deleteFileAssets(fileIds: number[]) {
  return request('/files', { method: 'DELETE', body: { fileIds }, schema: emptyEnvelopeSchema })
}

export function favoriteFileAssets(fileIds: number[]) {
  return request('/files/favorite', { method: 'PUT', body: { fileIds }, schema: emptyEnvelopeSchema })
}

export function addFileTagsBatch(fileIds: number[], tagIds: number[]) {
  return request('/files/tags/batch', { method: 'POST', body: { fileIds, tagIds }, schema: emptyEnvelopeSchema })
}

export function listFileFolders(parentFolderId = 0) {
  return request(`/files/folders?parentFolderId=${parentFolderId}`, { method: 'GET', schema: fileFolderTreeEnvelopeSchema })
}

export function createFileFolder(name: string, parentFolderId = 0) {
  return request('/files/folders', { method: 'POST', body: { name, parentFolderId }, schema: fileFolderEnvelopeSchema })
}

export function renameFileFolder(id: number, name: string) {
  return request(`/files/folders/${id}/name`, { method: 'PUT', body: { name }, schema: fileFolderEnvelopeSchema })
}

export function moveFileFolder(id: number, targetFolderId: number) {
  return request(`/files/folders/${id}/parent`, { method: 'PUT', body: { targetFolderId }, schema: fileFolderEnvelopeSchema })
}

export function deleteFileFolder(id: number) {
  return request(`/files/folders/${id}`, { method: 'DELETE', schema: emptyEnvelopeSchema })
}

export function moveFileAssets(fileIds: number[], targetFolderId: number) {
  return request('/files/folder', { method: 'PUT', body: { fileIds, targetFolderId }, schema: emptyEnvelopeSchema })
}
